<template>
    <div class="flex flex-col gap-4">
        <SliderRoot
            v-bind="$attrs"
            :model-value="modelValue"
            :max="max"
            :min="min"
            :step="step"
            class="relative flex items-center select-none touch-none w-full h-5"
            :aria-label="sliderLabel">
            <SliderTrack class="relative grow rounded-full h-2" :class="invalid ? 'bg-status-error-soft ring-1 ring-status-error' : 'bg-slider-track'">
                <SliderRange class="absolute rounded-full h-full slider-range" :class="invalid ? 'bg-status-error' : 'bg-control-accent'" />
            </SliderTrack>
            <SliderThumb
                v-bind="field?.controlAttrs.value"
                :id="thumbId ?? field?.controlId"
                :aria-labelledby="field?.labelId"
                class="block w-5 h-5 rounded-full shadow-xs slider-thumb"
                :class="invalid ? 'bg-status-error' : 'bg-control-accent'"
                :aria-valuetext="announcedAriaValueText" />
        </SliderRoot>
        <slot name="labels">
            <div v-if="labelStart || labelEnd" class="flex justify-between">
                <p v-if="labelStart" class="body-small text-control-label">{{ labelStart }}</p>
                <p v-if="labelEnd" class="body-small text-control-label">{{ labelEnd }}</p>
            </div>
        </slot>
    </div>
</template>

<script setup lang="ts">
import { invariant } from "~vue/utils";
import { SliderRange, SliderRoot, type SliderRootProps, SliderThumb, SliderTrack } from "reka-ui";
import { computed } from "vue";

import { useFormField } from "./formFieldContext";

const {
    max = 1,
    min = 0,
    step = 1,
    thumbValueLabel,
    modelValue,
} = defineProps<
    SliderRootProps & {
        // slider label for screen readers, required for accessibility when used bare; inside a FormField the field's label names the thumb
        sliderLabel?: string;
        // optional array of labels for each thumb value, used for aria-valuetext. Length must match the number of steps in the slider
        thumbValueLabel?: string[];
        labelStart?: string;
        labelEnd?: string;
        /** Id for the thumb. Inside a FormField, pass `id` to the FormField instead, so its label's `for` matches. */
        thumbId?: string;
    }
>();

// Inside a FormField the thumb, the focusable part, takes the field's id, describedby and invalid
// wiring, and is named through aria-labelledby since a label's `for` cannot reach a span. An explicit
// thumb-id still wins. While the field is invalid, track, range and thumb take the error colors.
const field = useFormField();
const invalid = computed(() => field?.invalid.value ?? false);

const currentStep = computed(() => (modelValue ?? [])[0] ?? 0);
const announcedAriaValueText = computed(() => thumbValueLabel?.[currentStep.value]);

if (thumbValueLabel) {
    invariant(thumbValueLabel.length === (max - min) / step + 1, "thumbValueLabel array length must match the number of steps in the slider");
}
</script>
